import {
  areaList,
  areaName,
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPES,
  assetTypeName,
  IMPORT_MAX_BYTES,
  ROUTE_CATEGORIES,
  ROUTE_CATEGORY_LABELS,
  ROUTE_STATUS_LABELS,
  ROUTE_STATUSES,
  type ImportCommitInput,
  type ImportPlacemark,
  type ImportPreview,
  type ImportReport,
  type ImportStatus,
} from '@ftthnms/shared';
import { Button, EmptyState } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Download,
  FileUp,
  Globe,
  House,
  List,
  Map as MapIcon,
  Route as RouteIcon,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ImportMap } from '../components/ImportMap';
import { useToast } from '../components/Toast';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, ApiError, nf, qs, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { download } from '../lib/download';
import { useSession } from '../lib/session';

type Tab = 'import' | 'export';

export function ImportExportPage() {
  const { ability } = useSession();
  const canImport = ability.can('import', 'Asset');
  const [tab, setTab] = useState<Tab>(canImport ? 'import' : 'export');
  return (
    <div className="fn-page-body">
      <div className="fn-tabs" role="tablist">
        {canImport && (
          <button
            type="button"
            role="tab"
            className="fn-tab"
            aria-selected={tab === 'import'}
            onClick={() => setTab('import')}
          >
            <Upload className="i i--sm" aria-hidden /> Import KMZ / KML
          </button>
        )}
        <button
          type="button"
          role="tab"
          className="fn-tab"
          aria-selected={tab === 'export'}
          onClick={() => setTab('export')}
        >
          <Download className="i i--sm" aria-hidden /> Export
        </button>
      </div>
      {tab === 'import' && canImport ? <ImportTab /> : <ExportTab />}
    </div>
  );
}

// ---------------------------------------------------------------- import

type Stage =
  | { kind: 'upload' }
  | { kind: 'preview'; preview: ImportPreview }
  | { kind: 'running'; preview: ImportPreview; status: ImportStatus };

const DEFAULT_OPTS: ImportCommitInput = {
  defaultType: 'ODP',
  folderTypes: {},
  namePrefix: '',
  status: 'active',
  area: null,
  fallbackArea: null,
  duplicate: 'skip',
  matchBy: 'name',
  proximityM: 10,
  importLines: true,
  lineSnapM: 30,
  lineStatus: 'installed',
  lineCategory: 'distribution',
  lineDanglingEnds: 'jc',
};

function ImportTab() {
  const [stage, setStage] = useState<Stage>({ kind: 'upload' });
  if (stage.kind === 'upload')
    return <UploadStep onPreview={(preview) => setStage({ kind: 'preview', preview })} />;
  if (stage.kind === 'preview') {
    return (
      <PreviewStep
        preview={stage.preview}
        onCancel={() => setStage({ kind: 'upload' })}
        onStarted={(status) => setStage({ kind: 'running', preview: stage.preview, status })}
      />
    );
  }
  return (
    <RunStep preview={stage.preview} initial={stage.status} onAgain={() => setStage({ kind: 'upload' })} />
  );
}

function UploadStep({ onPreview }: { onPreview: (p: ImportPreview) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  async function upload(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (!/\.(kmz|kml)$/i.test(file.name))
      return setError('Pilih file .kmz atau .kml (dari Google Earth / Google My Maps).');
    if (file.size > IMPORT_MAX_BYTES)
      return setError('File lebih dari 20 MB. Pecah per wilayah atau per folder.');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/v1/imports/preview', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      const body = (await res.json().catch(() => null)) as (ImportPreview & { message?: string }) | null;
      if (!res.ok || !body) throw new Error(body?.message ?? `Upload gagal (HTTP ${res.status}).`);
      if (body.placemarks.length === 0)
        throw new Error('File tidak berisi titik atau garis yang bisa diimpor.');
      onPreview(body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fn-card">
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          role="button"
          tabIndex={0}
          aria-label="Pilih file KMZ atau KML"
          onClick={() => input.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void upload(e.dataTransfer.files[0]);
          }}
          style={{
            border: `2px dashed ${over ? 'var(--primary)' : 'var(--border-muted)'}`,
            background: over ? 'var(--primary-subtle)' : 'var(--bg-surface-inset)',
            borderRadius: 'var(--radius-2xl)',
            padding: '48px 24px',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          <EmptyState
            icon={
              busy ? <span className="fn-spinner" aria-hidden /> : <FileUp className="i i--xl" aria-hidden />
            }
            title={busy ? 'Membaca file…' : 'Tarik file KMZ / KML ke sini'}
            text="Atau klik untuk memilih. Maks. 20 MB. Titik jadi aset, garis jadi jalur kabel. Data belum disimpan sampai Anda menekan Import."
          />
          <input
            ref={input}
            type="file"
            accept=".kmz,.kml"
            hidden
            onChange={(e) => void upload(e.target.files?.[0])}
          />
        </div>
        {error && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error}</div>
          </div>
        )}
        <p className="fn-hint">
          Tips: beri nama folder sesuai jenis (ODP, ODC, Tiang, Pelanggan…) supaya jenis aset terisi otomatis.
          Batas wilayah yang sudah digambar dipakai untuk mengisi wilayah.
        </p>
      </div>
    </section>
  );
}

function PreviewStep({
  preview,
  onCancel,
  onStarted,
}: {
  preview: ImportPreview;
  onCancel: () => void;
  onStarted: (s: ImportStatus) => void;
}) {
  const toast = useToast();
  const [opts, setOpts] = useState<ImportCommitInput>(() => ({
    ...DEFAULT_OPTS,
    folderTypes: Object.fromEntries(
      preview.folders.map((f) => [f.path, f.points === 0 ? 'auto' : (f.suggested ?? 'auto')]),
    ),
  }));
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof ImportCommitInput>(k: K, v: ImportCommitInput[K]) =>
    setOpts((o) => ({ ...o, [k]: v }));

  const typeOf = useCallback(
    (p: ImportPlacemark): string => {
      const mapped = opts.folderTypes[p.folder] ?? 'auto';
      if (mapped === 'skip') return 'skip';
      if (p.kind === 'line') return opts.importLines ? 'line' : 'skip';
      return mapped === 'auto' ? (p.suggested ?? opts.defaultType) : mapped;
    },
    [opts.folderTypes, opts.importLines, opts.defaultType],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const p of preview.placemarks) {
      const t = typeOf(p);
      c[t] = (c[t] ?? 0) + 1;
    }
    return c;
  }, [preview.placemarks, typeOf]);
  const points = preview.placemarks.filter((p) => p.kind === 'point').length;
  const lines = preview.placemarks.length - points;
  const toImport = preview.placemarks.length - (counts.skip ?? 0);

  async function start() {
    setBusy(true);
    try {
      onStarted(await send<ImportStatus>('POST', `/api/v1/imports/${preview.id}/commit`, opts));
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
      if (err instanceof ApiError && err.code === 'IMPORT_EXPIRED') onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'minmax(0, 420px) minmax(0, 1fr)', alignItems: 'start' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <section className="fn-card">
          <div className="fn-card__header">
            <div>
              <h3 className="fn-card__title">{preview.fileName}</h3>
              <p className="fn-card__desc">
                {nf.format(points)} titik · {nf.format(lines)} garis · {nf.format(preview.folders.length)}{' '}
                folder
                {preview.unsupported ? ` · ${preview.unsupported} poligon/track diabaikan` : ''}
              </p>
            </div>
          </div>
          <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="fn-label">Folder → jenis aset</span>
            {preview.folders.map((f) => (
              <div
                key={f.path}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 150px',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      font: '600 12.5px/18px var(--font-sans)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={f.path}
                  >
                    {f.path || '(tanpa folder)'}
                  </div>
                  <div className="fn-hint">
                    {f.points ? `${nf.format(f.points)} titik` : ''}
                    {f.points && f.lines ? ' · ' : ''}
                    {f.lines ? `${nf.format(f.lines)} garis` : ''}
                  </div>
                </div>
                <select
                  className="fn-input"
                  aria-label={`Jenis untuk folder ${f.path || 'tanpa folder'}`}
                  value={opts.folderTypes[f.path] ?? 'auto'}
                  onChange={(e) => set('folderTypes', { ...opts.folderTypes, [f.path]: e.target.value })}
                >
                  <option value="auto">{f.points === 0 ? 'Impor' : 'Otomatis'}</option>
                  {f.points > 0 &&
                    ASSET_TYPES.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.name}
                      </option>
                    ))}
                  <option value="skip">Lewati</option>
                </select>
              </div>
            ))}
            <Field
              label="Jenis bila tidak terdeteksi"
              hint="Untuk folder Otomatis yang nama titiknya tidak dikenali"
            >
              <select
                className="fn-input"
                value={opts.defaultType}
                onChange={(e) => set('defaultType', e.target.value)}
              >
                {ASSET_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="fn-card">
          <div className="fn-card__header">
            <h3 className="fn-card__title">Opsi import</h3>
          </div>
          <div className="fn-card__body fn-form-grid">
            <Field label="Prefix nama" hint="Mis. “CPK ” di depan nama">
              <input
                className="fn-input"
                value={opts.namePrefix}
                maxLength={30}
                onChange={(e) => set('namePrefix', e.target.value)}
                placeholder="(tanpa)"
              />
            </Field>
            <Field label="Status aset">
              <select
                className="fn-input"
                value={opts.status}
                onChange={(e) => set('status', e.target.value as 'planned' | 'active')}
              >
                <option value="active">{ASSET_STATUS_LABELS.active}</option>
                <option value="planned">{ASSET_STATUS_LABELS.planned}</option>
              </select>
            </Field>
            <Field label="Wilayah">
              <select
                className="fn-input"
                value={opts.area ?? ''}
                onChange={(e) => set('area', e.target.value || null)}
              >
                <option value="">Dari batas wilayah</option>
                {areaList().map((a) => (
                  <option key={a.code} value={a.code}>
                    Semua ke {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Wilayah cadangan" hint="Untuk titik di luar batas wilayah">
              <select
                className="fn-input"
                value={opts.fallbackArea ?? ''}
                disabled={!!opts.area}
                onChange={(e) => set('fallbackArea', e.target.value || null)}
              >
                <option value="">Lewati titik tsb.</option>
                {areaList().map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Bila sudah ada">
              <select
                className="fn-input"
                value={opts.duplicate}
                onChange={(e) => set('duplicate', e.target.value as ImportCommitInput['duplicate'])}
              >
                <option value="skip">Lewati</option>
                <option value="overwrite">Timpa lokasi & keterangan</option>
                <option value="new">Tetap buat baru</option>
              </select>
            </Field>
            <Field label="Dianggap sama bila">
              <select
                className="fn-input"
                value={opts.matchBy}
                disabled={opts.duplicate === 'new'}
                onChange={(e) => set('matchBy', e.target.value as 'name' | 'proximity')}
              >
                <option value="name">Jenis & nama sama</option>
                <option value="proximity">Jenis sama, ≤ {opts.proximityM} m</option>
              </select>
            </Field>
            {lines > 0 && (
              <>
                <label className="fn-choice span-2" style={{ cursor: 'pointer' }}>
                  <span>
                    <span className="fn-choice__title">Garis jadi jalur kabel</span>
                    <span className="fn-choice__desc">
                      Ujung garis ditempel ke aset terdekat (≤ {opts.lineSnapM} m), termasuk aset dari file
                      ini. Bentuk dan warna garis dari Google Earth dipertahankan.
                    </span>
                  </span>
                  <span className="fn-switch">
                    <input
                      className="fn-switch__input"
                      type="checkbox"
                      checked={opts.importLines}
                      onChange={(e) => set('importLines', e.target.checked)}
                    />
                    <span className="fn-switch__track" />
                  </span>
                </label>
                {opts.importLines && (
                  <>
                    <Field label="Status jalur">
                      <select
                        className="fn-input"
                        value={opts.lineStatus}
                        onChange={(e) => set('lineStatus', e.target.value as ImportCommitInput['lineStatus'])}
                      >
                        {ROUTE_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {ROUTE_STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Kategori jalur">
                      <select
                        className="fn-input"
                        value={opts.lineCategory}
                        onChange={(e) =>
                          set('lineCategory', e.target.value as ImportCommitInput['lineCategory'])
                        }
                      >
                        {ROUTE_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {ROUTE_CATEGORY_LABELS[c]}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Ujung garis tanpa aset">
                      <select
                        className="fn-input"
                        value={opts.lineDanglingEnds}
                        onChange={(e) =>
                          set('lineDanglingEnds', e.target.value as ImportCommitInput['lineDanglingEnds'])
                        }
                      >
                        <option value="jc">Pasang JC di ujungnya (garis tetap masuk)</option>
                        <option value="skip">Lewati garisnya</option>
                      </select>
                    </Field>
                  </>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        <section className="fn-card">
          <div className="fn-card__header">
            <div>
              <h3 className="fn-card__title">Akan diimpor</h3>
              <p className="fn-card__desc">
                Pudar = dilewati. Duplikat dan titik di luar wilayah dilaporkan setelah import.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={onCancel} disabled={busy}>
                Ganti file
              </Button>
              <Button
                variant="primary"
                icon={<Upload className="i" aria-hidden />}
                loading={busy}
                disabled={toImport === 0}
                onClick={() => void start()}
              >
                Import {nf.format(toImport)}
              </Button>
            </div>
          </div>
          <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ASSET_TYPES.filter((t) => counts[t.code]).map((t) => (
                <span key={t.code} className="fn-badge" style={{ gap: 6 }}>
                  <AssetGlyph type={t.code} /> {t.name}{' '}
                  <strong className="num">{nf.format(counts[t.code]!)}</strong>
                </span>
              ))}
              {counts.line ? (
                <span className="fn-badge fn-badge--primary">
                  <RouteIcon className="i i--sm" aria-hidden /> Jalur kabel{' '}
                  <strong className="num">{nf.format(counts.line)}</strong>
                </span>
              ) : null}
              {counts.skip ? <span className="fn-badge">Dilewati {nf.format(counts.skip)}</span> : null}
            </div>
            <ImportMap placemarks={preview.placemarks} typeOf={typeOf} />
          </div>
        </section>
        <PlacemarkTable placemarks={preview.placemarks} typeOf={typeOf} />
      </div>
    </div>
  );
}

function PlacemarkTable({
  placemarks,
  typeOf,
}: {
  placemarks: ImportPlacemark[];
  typeOf: (p: ImportPlacemark) => string;
}) {
  const [limit, setLimit] = useState(100);
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Nama di file</th>
              <th>Folder</th>
              <th>Akan jadi</th>
              <th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {placemarks.slice(0, limit).map((p) => {
              const t = typeOf(p);
              return (
                <tr key={p.i} style={{ opacity: t === 'skip' ? 0.45 : 1 }}>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</td>
                  <td>{p.folder || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {t === 'skip' ? 'Dilewati' : t === 'line' ? 'Jalur kabel' : assetTypeName(t)}
                  </td>
                  <td
                    style={{
                      maxWidth: 280,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={p.description ?? undefined}
                  >
                    {p.description ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {placemarks.length > limit && (
        <div className="fn-table-footer">
          <span>
            Menampilkan {nf.format(limit)} dari {nf.format(placemarks.length)}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setLimit((l) => l + 200)}>
            Tampilkan lagi
          </Button>
        </div>
      )}
    </section>
  );
}

function RunStep({
  preview,
  initial,
  onAgain,
}: {
  preview: ImportPreview;
  initial: ImportStatus;
  onAgain: () => void;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ImportStatus>(initial);

  useEffect(() => {
    if (status.state !== 'running') return;
    const t = setTimeout(() => {
      api<ImportStatus>(`/api/v1/imports/${preview.id}`)
        .then((s) => {
          setStatus(s);
          if (s.state === 'done') void invalidateAssets(qc);
        })
        .catch((err: Error) => setStatus({ state: 'failed', error: err.message }));
    }, 800);
    return () => clearTimeout(t);
  }, [status, preview.id, qc]);

  if (status.state === 'running') {
    const pct = status.total > 0 ? Math.round((status.done / status.total) * 100) : 0;
    return (
      <section className="fn-card">
        <div
          className="fn-card__body"
          style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 32 }}
        >
          <h3 className="fn-card__title">Mengimpor {preview.fileName}…</h3>
          <div className="fn-capacity">
            <div className="fn-capacity__legend">
              <span>Diproses</span>
              <span>
                <strong>{nf.format(status.done)}</strong>/{nf.format(status.total)} · {pct}%
              </span>
            </div>
            <div className="fn-capacity__track">
              <span className="fn-capacity__used" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <p className="fn-hint">
            Semua data disimpan sekaligus di akhir; kalau gagal, tidak ada yang tersimpan setengah jalan.
          </p>
        </div>
      </section>
    );
  }
  if (status.state === 'failed') {
    return (
      <section className="fn-card">
        <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">
              <span className="fn-alert__title">Import gagal, tidak ada data yang disimpan</span>
              <span className="fn-alert__text">{status.error}</span>
            </div>
          </div>
          <Button onClick={onAgain} style={{ alignSelf: 'flex-start' }}>
            Coba lagi
          </Button>
        </div>
      </section>
    );
  }
  return <Report report={status.report} fileName={preview.fileName} onAgain={onAgain} />;
}

function Report({
  report: r,
  fileName,
  onAgain,
}: {
  report: ImportReport;
  fileName: string;
  onAgain: () => void;
}) {
  const kpis: Array<[string, number, string]> = [
    ['Aset baru', r.assetsCreated, 'success'],
    ['Aset diperbarui', r.assetsUpdated, 'primary'],
    ['Jalur kabel baru', r.routesCreated, 'cyan'],
    ['Dilewati', r.skippedTotal, 'warning'],
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="fn-alert fn-alert--success" role="status">
        <div className="fn-alert__body">
          <span className="fn-alert__title">Import {fileName} selesai</span>
          <span className="fn-alert__text" style={{ color: 'inherit' }}>
            {Object.entries(r.byType)
              .map(([t, n]) => `${nf.format(n)} ${assetTypeName(t)}`)
              .join(' · ') || 'Tidak ada aset baru'}
          </span>
        </div>
        <Link to="/map" className="fn-btn fn-btn--secondary fn-btn--sm">
          <MapIcon className="i" aria-hidden /> Lihat di Peta
        </Link>
        <Button size="sm" onClick={onAgain}>
          Import file lain
        </Button>
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {kpis.map(([label, n, tone]) => (
          <div key={label} className="fn-card fn-kpi">
            <div className="fn-kpi__head">
              <span className="fn-kpi__label">{label}</span>
              <span className={`fn-tile fn-tile--${tone}`}>
                <List className="i" aria-hidden />
              </span>
            </div>
            <div className="fn-kpi__value">
              <span className="fn-kpi__number">{nf.format(n)}</span>
            </div>
          </div>
        ))}
      </div>
      {r.skipped.length > 0 && (
        <section className="fn-card" style={{ overflow: 'hidden' }}>
          <div className="fn-card__header">
            <div>
              <h3 className="fn-card__title">Dilewati</h3>
              <p className="fn-card__desc">
                {r.skippedTotal > r.skipped.length
                  ? `${nf.format(r.skipped.length)} pertama dari ${nf.format(r.skippedTotal)}`
                  : `${nf.format(r.skippedTotal)} baris`}
              </p>
            </div>
          </div>
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Folder</th>
                  <th>Alasan</th>
                </tr>
              </thead>
              <tbody>
                {r.skipped.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.name}</td>
                    <td>{s.folder || '—'}</td>
                    <td>{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- export

function ExportTab() {
  const { area, ability } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [routeStatus, setRouteStatus] = useState('');
  const [withRoutes, setWithRoutes] = useState(true);

  async function run(key: string, path: string) {
    setBusy(key);
    try {
      await download(path, 'ftthnms-export');
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  const scope = area ? areaName(area) : 'semua wilayah yang bisa Anda lihat';
  const table = (entity: string, format: string, extra: Record<string, string> = {}) =>
    `/api/v1/exports/table${qs({ entity, format, area, ...extra })}`;
  const buttons = (key: string, entity: string, extra: Record<string, string> = {}) => (
    <div style={{ display: 'flex', gap: 8 }}>
      <Button
        size="sm"
        variant="primary"
        icon={<Download className="i" aria-hidden />}
        loading={busy === `${key}-xlsx`}
        onClick={() => void run(`${key}-xlsx`, table(entity, 'xlsx', extra))}
      >
        Excel
      </Button>
      <Button
        size="sm"
        loading={busy === `${key}-csv`}
        onClick={() => void run(`${key}-csv`, table(entity, 'csv', extra))}
      >
        CSV
      </Button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="fn-card__desc">
        Data diekspor untuk <strong>{scope}</strong> (ubah lewat pemilih wilayah di atas). Setiap ekspor
        dicatat di audit log.
      </p>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <ExportCard
          icon={<List className="i" aria-hidden />}
          tone="primary"
          title="Daftar aset"
          desc="Sheet semua aset + satu sheet per jenis berisi data teknisnya (splitter, kapasitas, IP, pelanggan…)."
        >
          <div className="fn-form-grid">
            <Field label="Jenis">
              <select className="fn-input" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Semua jenis</option>
                {ASSET_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select className="fn-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Semua status</option>
                {ASSET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ASSET_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {buttons('assets', 'assets', { type, status })}
        </ExportCard>

        {ability.can('export', 'Route') && (
          <ExportCard
            icon={<RouteIcon className="i" aria-hidden />}
            tone="success"
            title="Jalur kabel"
            desc="Asal–tujuan, kabel, core, panjang jalur dan kabel (dengan slack), tiang otomatis."
          >
            <Field label="Status">
              <select
                className="fn-input"
                value={routeStatus}
                onChange={(e) => setRouteStatus(e.target.value)}
              >
                <option value="">Semua status</option>
                {ROUTE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ROUTE_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            {buttons('routes', 'routes', { routeStatus })}
          </ExportCard>
        )}

        <ExportCard
          icon={<House className="i" aria-hidden />}
          tone="cyan"
          title="Pelanggan per ODP"
          desc="Setiap pelanggan dengan ODP, port ODP, ODC, SN, paket dan status layanan. Pelanggan yang belum terhubung ada di akhir."
        >
          {buttons('customers', 'customers')}
        </ExportCard>

        <ExportCard
          icon={<Globe className="i" aria-hidden />}
          tone="purple"
          title="Google Earth (KMZ)"
          desc="Folder per jenis aset dan per status jalur, warna seperti di peta. Bisa diimpor kembali ke FTTH NMS."
        >
          <Field label="Jenis aset">
            <select className="fn-input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Semua jenis</option>
              {ASSET_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {ability.can('export', 'Route') && (
            <label className="fn-choice" style={{ cursor: 'pointer' }}>
              <span className="fn-choice__title">Sertakan jalur kabel</span>
              <span className="fn-switch">
                <input
                  className="fn-switch__input"
                  type="checkbox"
                  checked={withRoutes}
                  onChange={(e) => setWithRoutes(e.target.checked)}
                />
                <span className="fn-switch__track" />
              </span>
            </label>
          )}
          <div>
            <Button
              size="sm"
              variant="primary"
              icon={<Download className="i" aria-hidden />}
              loading={busy === 'kmz'}
              onClick={() =>
                void run(
                  'kmz',
                  `/api/v1/exports/kmz${qs({ area, type, routes: withRoutes ? 1 : undefined })}`,
                )
              }
            >
              KMZ
            </Button>
          </div>
        </ExportCard>
      </div>
      <p className="fn-hint">
        BOQ / RAB dan daftar harga aset diekspor dari halaman{' '}
        <Link to="/cost" className="fn-link">
          Biaya & BOQ
        </Link>
        .
      </p>
    </div>
  );
}

function ExportCard({
  icon,
  tone,
  title,
  desc,
  children,
}: {
  icon: ReactNode;
  tone: string;
  title: string;
  desc: string;
  children: ReactNode;
}) {
  return (
    <section className="fn-card">
      <div className="fn-card__header">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span className={`fn-tile fn-tile--${tone}`}>{icon}</span>
          <div>
            <h3 className="fn-card__title">{title}</h3>
            <p className="fn-card__desc">{desc}</p>
          </div>
        </div>
      </div>
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </section>
  );
}

/** The wrapping <label> names the control inside it. */
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="fn-field">
      <span className="fn-label">{label}</span>
      {children}
      {hint && <span className="fn-hint">{hint}</span>}
    </label>
  );
}
